import type { AnimationEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';

import { zIndexMap } from '../../../shared/config';
import { useSheetPresence } from '../../../shared/hooks/use-sheet-presence';
import { BaseButton, PhoneEntryField, PrimaryButton } from '../../../shared/ui';
import {
  resolveTipCheckoutPayerContactSkipCtaKey,
  resolveTipCheckoutPayerContactSkipHintKey,
  TIP_CHECKOUT_SHEET_CLOSE_ANIMATION_MS,
} from '../model';

import { useTipCheckoutPayerContactForm } from './tip-checkout-payer-contact-sheet.logic';

type TipCheckoutPayerContactSheetProps = {
  isOpen: boolean;
  isSubmitting?: boolean;
  onContinue: (phoneNumber?: string) => void;
  onClose: () => void;
  submitError?: string | null;
};

const overlayFadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const overlayFadeOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const sheetSlideIn = keyframes`
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
`;

const sheetSlideOut = keyframes`
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(100%);
  }
`;

const getOverlayAnimationCss = (isClosing: boolean) =>
  isClosing
    ? css`
        animation: ${overlayFadeOut} ${TIP_CHECKOUT_SHEET_CLOSE_ANIMATION_MS}ms
          ease-in both;
      `
    : css`
        animation: ${overlayFadeIn} 300ms ease-out both;
      `;

const getSheetAnimationCss = (isClosing: boolean) =>
  isClosing
    ? css`
        animation: ${sheetSlideOut} ${TIP_CHECKOUT_SHEET_CLOSE_ANIMATION_MS}ms
          ease-in both;
      `
    : css`
        animation: ${sheetSlideIn} 360ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
      `;

const Overlay = styled.div.attrs({
  'data-ui': 'tips-checkout-payer-contact-overlay',
})<{ $isClosing: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-top: ${({ theme }) =>
    `max(${theme.scale.px(65)}, env(safe-area-inset-top))`};
  background: ${({ theme }) => theme.colors.surface.backdrop};
  overscroll-behavior: contain;
  touch-action: pan-y;
  z-index: ${zIndexMap.tipsCheckout.overlay};
  ${({ $isClosing }) => getOverlayAnimationCss($isClosing)};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Sheet = styled.div.attrs({
  'data-ui': 'tips-checkout-payer-contact-sheet',
})<{ $isClosing: boolean }>`
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-top-left-radius: ${({ theme }) => theme.scale.px(21)};
  border-top-right-radius: ${({ theme }) => theme.scale.px(21)};
  padding: ${({ theme }) => theme.scale.px(59)}
    ${({ theme }) => theme.scale.px(31)} ${({ theme }) => theme.scale.px(34)};
  background: ${({ theme }) => theme.colors.surface.base};
  z-index: ${zIndexMap.tipsCheckout.sheet};
  ${({ $isClosing }) => getSheetAnimationCss($isClosing)};

  & > [data-ui='tips-checkout-payer-contact-phone-entry'] {
    margin-top: ${({ theme }) => theme.scale.px(25)};
  }

  & > [data-ui='tips-checkout-payer-contact-skip-hint'] {
    margin-top: ${({ theme }) => theme.scale.px(6)};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Title = styled.h2`
  width: 100%;
  margin: 0;
  font-family: Rubik, sans-serif;
  font-size: ${({ theme }) => theme.scale.px(24)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(28)};
`;

const Subtitle = styled.p`
  width: 100%;
  margin: ${({ theme }) => theme.scale.px(9)} 0 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
  line-height: ${({ theme }) => theme.scale.px(15)};
`;

const StatusMessage = styled.p.attrs({
  'data-ui': 'tips-checkout-payer-contact-status',
})`
  width: 100%;
  margin: ${({ theme }) => theme.scale.px(12)} 0 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(17)};
  color: ${({ theme }) => theme.colors.support.danger};
  text-align: center;
`;

const PrimaryAction = styled(PrimaryButton)`
  margin-top: ${({ theme }) => theme.scale.px(71)};

  &:disabled {
    opacity: 0.4;
  }
`;

const SecondaryAction = styled(BaseButton)`
  width: ${({ theme }) => theme.scale.px(331)};
  min-height: ${({ theme }) => theme.scale.px(56)};
  margin-top: ${({ theme }) => theme.scale.px(10)};
  border-radius: ${({ theme }) => theme.scale.px(10)};
  border: ${({ theme }) => theme.scale.px(2)} solid
    ${({ theme }) => theme.colors.support.infoSubtle};
  font-family: Rubik, sans-serif;
  font-size: ${({ theme }) => theme.scale.px(15)};
  font-weight: 600;
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.text.subdued};

  &:disabled {
    opacity: 0.4;
  }
`;

const SecondaryHint = styled.p.attrs({
  'data-ui': 'tips-checkout-payer-contact-skip-hint',
})`
  width: ${({ theme }) => theme.scale.px(163)};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
  line-height: ${({ theme }) => theme.scale.px(15)};
  text-align: center;
  white-space: pre-line;
`;

export function TipCheckoutPayerContactSheet({
  isOpen,
  isSubmitting = false,
  onContinue,
  onClose,
  submitError = null,
}: TipCheckoutPayerContactSheetProps) {
  const { t } = useTranslation();
  const { handleCloseAnimationEnd, isClosing, isMounted } =
    useSheetPresence(isOpen);
  const phoneForm = useTipCheckoutPayerContactForm();
  const skipCtaKey = resolveTipCheckoutPayerContactSkipCtaKey(
    phoneForm.countryCode,
  );
  const skipHintKey = resolveTipCheckoutPayerContactSkipHintKey(
    phoneForm.countryCode,
  );

  const stopOverlayClose = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const closeSheet = () => {
    if (isSubmitting) {
      return;
    }

    onClose();
  };

  const handleSheetAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || !isClosing) {
      return;
    }

    handleCloseAnimationEnd();
  };

  if (!isMounted) {
    return null;
  }

  return (
    <Overlay $isClosing={isClosing} onClick={closeSheet}>
      <Sheet
        $isClosing={isClosing}
        aria-busy={isSubmitting}
        onAnimationEnd={handleSheetAnimationEnd}
        onClick={stopOverlayClose}
      >
        <Title>{t('login.title')}</Title>
        <Subtitle>{t('login.subtitle')}</Subtitle>
        <PhoneEntryField
          clearPhoneAriaLabel={t('login.clearPhoneAria')}
          countryCode={phoneForm.countryCode}
          countryCodeAriaLabel={t('login.countryCodeAria')}
          countryFlag={phoneForm.countryFlag}
          countryOptions={phoneForm.countryOptions}
          dataUi="tips-checkout-payer-contact-phone-entry"
          onClearPhone={phoneForm.clearPhone}
          onPhoneChange={phoneForm.setPhone}
          onSelectCountryCode={phoneForm.selectCountryCode}
          phoneAriaLabel={t('login.phoneAria')}
          phoneMask={phoneForm.phoneMask}
          phoneValue={phoneForm.phone}
        />
        {submitError ? (
          <StatusMessage aria-live="polite" role="alert">
            {submitError}
          </StatusMessage>
        ) : null}
        <SecondaryHint>{t(skipHintKey)}</SecondaryHint>
        <PrimaryAction
          disabled={!phoneForm.isPhoneComplete || isSubmitting}
          type="button"
          onClick={() => {
            onContinue(phoneForm.phoneNumber ?? undefined);
          }}
        >
          {t('login.cta')}
        </PrimaryAction>
        <SecondaryAction
          disabled={isSubmitting}
          type="button"
          onClick={() => {
            onContinue();
          }}
        >
          {t(skipCtaKey)}
        </SecondaryAction>
      </Sheet>
    </Overlay>
  );
}
